import {gql} from '@apollo/client';
import React from 'react';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {
  MetadataRegistryButtonQuery,
  MetadataRegistryButtonQueryVariables,
} from './__generated/MetadataRegistryButtonQuery';

export const METADATA_TOGGLE_REGISTER_BUTTON_QUERY = gql`
  query MetadataRegistryButtonQuery($registryId: StrictString!) @engComponent(name: REGISTRY) @skipAudit {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        permissions {
          appendable
        }
      }
    }
  }
`;

export interface MetadataToggleRegisterButtonProps {
  /** API ID of the registry-compatible file */
  fileId: string;
  /** User permissions for the file */
  permissions: Permissions;
  /** API ID of the registry that the file is already registered in */
  registryId: string | null;
  /** API ID of the registry that the file's tag schema is associated with */
  schemaRegistryId: string | null;
  /** User permissions for the file's tag schema if one exists.  */
  schemaPermissions: {objectsRegisterable: boolean} | null;
  /** Indicate if onRegister should not be called due to validation errors.  */
  disableRegistering: boolean;
  /** Callback to perform post-submission validation and register. */
  onRegister: () => void;
  /** Callback to perform unregister */
  onUnregister: () => void;
  /**
   * When true, suppress the inline Unregister button while still rendering Register inline. Used when
   * Unregister has been relocated into an overflow ("More") menu by the caller.
   */
  hideUnregisterButton?: boolean;
}

/**
 * An un/register button for a registry-compatible file.
 */
export default function MetadataToggleRegisterButton({
  fileId,
  permissions,
  registryId,
  schemaRegistryId,
  schemaPermissions,
  onRegister,
  disableRegistering,
  onUnregister,
  hideUnregisterButton = false,
}: MetadataToggleRegisterButtonProps): JSX.Element | null {
  const queryResult = useSkippableBenchlingQuery<
    MetadataRegistryButtonQuery,
    MetadataRegistryButtonQueryVariables
  >(
    METADATA_TOGGLE_REGISTER_BUTTON_QUERY,
    schemaRegistryId != null ? {variables: {registryId: schemaRegistryId}} : {skip: true}
  );

  const shouldShowRegister =
    // (1) File is not already registered
    registryId == null &&
    // (2) A schema policy allows the user to register the file.
    schemaPermissions != null &&
    schemaPermissions.objectsRegisterable &&
    // (3) A registry policy allows the user to register the file/
    queryResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(queryResult.data.registry) &&
    queryResult.data.registry.permissions.appendable &&
    // (4) User has the correct item permissions based on capability management
    isFeatureEnabled({
      feature: RegistryFeature.REGISTRY_REGISTER,
      item: {
        id: fileId,
        permissions,
      },
    });

  // User has the correct item permissions based on capability management
  const shouldShowUnregister = isFeatureEnabled({
    feature: RegistryFeature.REGISTRY_UNREGISTER,
    item: {
      id: fileId,
      permissions,
    },
  });
  if (shouldShowRegister) {
    // TODO(BNCH-123241): Always show the register button if the user has access to the Registry product. Use
    // a disabled state with a tooltip why the user cannot register yet (either, "Checking permissions..." or
    // the specific reason from shouldShowRegister implementation).
    const disabledReason = disableRegistering ? 'Fix errors before registering.' : null;
    return (
      <Tooltip
        container="body"
        placement={TooltipPlacement.TOP}
        title={disabledReason}
        disabled={!disabledReason}
      >
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          className="metadata-header-button-spacing-left"
          key="register"
          onClick={onRegister}
          size={ButtonSize.SMALL}
          text="Register"
          disabled={disabledReason != null}
          dataTestAttrs={{component: 'MetadataEditorToolbar', element: 'registerButton'}}
        />
      </Tooltip>
    );
  } else if (shouldShowUnregister && !hideUnregisterButton) {
    return (
      <Tooltip container="body" placement={TooltipPlacement.TOP} title="Unregister">
        <Button
          aria-label="Unregister"
          buttonStyle={ButtonStyle.NEGATIVE}
          className="metadata-header-button-spacing-left"
          icon="unregister"
          key="unregister"
          onClick={onUnregister}
          size={ButtonSize.SMALL}
          dataTestAttrs={{component: 'MetadataEditorToolbar', element: 'unregisterButton'}}
        />
      </Tooltip>
    );
  }
  return null;
}
